/** @jsx jsx */
import React, { useState, useMemo, useEffect } from 'react'
import * as Sentry from '@sentry/browser'
import { jsx } from 'theme-ui'
import {
  Txt,
  Box,
  FormattedCurrency,
  Heading,
  Button,
  Row,
  Col,
  Collapse,
  FormGroupLabel,
  FormGroupDivider,
  FormGroup,
  FormGroupInput,
  LoadingPlaceholder,
} from '../../components'
import { Positioned } from '../../components/Positioned'
import { IUserPositioning, IPositionProps } from './positionProps'
import { theme } from '../../gatsby-plugin-theme-ui/theme'
import { Ellipsis } from '../../components/Ellipsis'
import { useInterval, useMount } from 'react-use'
import { UserProfilePicture } from '../../components/UserProfilePicture'
import { ButtonBlock } from '../../components/ButtonBlock'
import { Modal } from '../../components/Modal'
import { FormattedUserName } from '../../components/FormattedUserName'
import {
  ClientGameHandController,
  IClientUserData,
} from '../../controllers/ClientGameHandController'
import { IGameActions } from './types'
import { lastCardContent } from '../../components/Card'
import { Form, Formik } from 'formik'
import { ConnectedFormGroup, ConnectedFormGroupDivider } from '../../components/ConnectedFormField'
import { FormErrorMessage, GeneralFormErrorMessage } from '../../components/FormErrorMessage'
import * as Yup from 'yup'
import _ from 'lodash'
import { AsyncActionButton } from '../../components/AsyncActionButton'
import { captureExceptionWithLabel } from '../../utils/sentry'
import { ID } from '../../sharedBetweenServerAndClient__DO_NOT_EDIT/types'
import { maybePlural } from '../../utils/formatting'
import { GiveUserAdditionalChipsForm } from './GiveUserAdditionalChipsForm'

export const otherUserHandWidth = [50, 75, 100]

const OtherPlayerInformationModal: React.SFC<{
  userData: IClientUserData
  controller: ClientGameHandController
  actions: IGameActions
}> = ({ userData, controller, actions }) => {
  const [isShowingGiveChipsForm, setIsShowingGiveChipsForm] = useState(false)
  return (
    <Box>
      <Box sx={{ maxWidth: 300, mx: 'auto' }}>
        <Txt>
          {(() => {
            switch (userData.tableState) {
              case 'AWAY':
                return 'Away (set to auto-check/auto-fold)'
              case 'OUT':
                return 'Out of the game'
              case 'PLAYING':
                return 'Currently playing'
            }
          })()}
        </Txt>
        <Txt>
          <Txt as="span" sx={{ fontWeight: 'bold' }}>
            Chips in current pot:{' '}
          </Txt>
          <FormattedCurrency amount={userData.amountInvested} />
        </Txt>
        <Txt>
          <Txt as="span" sx={{ fontWeight: 'bold' }}>
            Chips left:{' '}
          </Txt>
          <FormattedCurrency amount={userData.amountLeftToBet} /> (
          {Math.floor((userData.amountLeftToBet / controller.totalChips) * 100)}% of table)
        </Txt>
        <Txt>
          <Txt as="span" sx={{ fontWeight: 'bold' }}>
            Total chips given:{' '}
          </Txt>
          <FormattedCurrency amount={userData.totalAmountGiven} />
        </Txt>
        {!!userData.pendingAmountGiven && (
          <Txt sx={{ color: 'lightGreen' }}>
            <Txt as="span" sx={{ fontWeight: 'bold' }}>
              Pending chips given next hand:{' '}
            </Txt>
            <FormattedCurrency amount={userData.pendingAmountGiven} />
          </Txt>
        )}
        {!!userData.votesToSetUserToAway.length && (
          <Txt sx={{ mt: 2, fontWeight: 'bold' }}>
            {userData.votesToSetUserToAway.length}{' '}
            {maybePlural('vote', userData.votesToSetUserToAway)} to set to away (
            {Math.ceil(controller.usersInGameTablePosition.length * 0.5) -
              userData.votesToSetUserToAway.length +
              1}{' '}
            more needed)
          </Txt>
        )}
      </Box>
      <Row>
        <Col
          sx={{ mt: [3, null, null, 5] }}
          xs={{ size: 12, offset: 0 }}
          md={{ size: 6, offset: 0 }}
        >
          <AsyncActionButton
            block
            sx={{ color: 'darkBlue', background: 'transparent' }}
            loadingVariant="light"
            disabled={_.includes(userData.votesToSetUserToAway, controller.currentUserId)}
            errorLabel="voteToSetUserTableState"
            action={() => actions.voteToSetUserTableState({ userId: userData.id, state: 'AWAY' })}
          >
            Vote to set to away
          </AsyncActionButton>
        </Col>
        {controller.currentUserData.role === 'ADMIN' && (
          <Col sx={{ mt: [3, null, null, 5] }} xs={12} md={6}>
            <Button
              sx={{ color: 'darkBlue', background: 'transparent' }}
              block
              onClick={() => setIsShowingGiveChipsForm(!isShowingGiveChipsForm)}
            >
              Give additional chips
            </Button>
          </Col>
        )}
      </Row>
      <Collapse isOpened={isShowingGiveChipsForm}>
        <Box sx={{ pt: 5 }}>
          <GiveUserAdditionalChipsForm
            onSuccess={() => {
              setIsShowingGiveChipsForm(false)
            }}
            {...{ userData, actions }}
          />
        </Box>
      </Collapse>
    </Box>
  )
}

const BlinkingProfilePicture: React.SFC<
  { userData: IClientUserData } & Pick<IPositionProps, 'measurements'>
> = ({ userData, measurements }) => {
  const [hasBorderLit, setHasBorderLit] = useState(false)

  useEffect(() => {
    if (userData.isNextToAct !== hasBorderLit) {
      setHasBorderLit(userData.isNextToAct)
    }
  }, [userData.isNextToAct])
  useInterval(
    () => {
      setHasBorderLit(!hasBorderLit)
    },
    userData.isNextToAct ? 2000 : null,
  )
  return (
    <UserProfilePicture
      width={measurements.offTableUserInfoWidth / 1.75}
      user={userData}
      tableState={userData.tableState}
      sx={{
        borderColor: hasBorderLit ? 'lightGreen' : 'darkBlue',
        borderWidth: ['4px', null, null, '8px'],
        borderStyle: 'solid',
      }}
    />
  )
}

export const OtherPlayerInformation: React.FC<
  Pick<IPositionProps, 'measurements'> & {
    actions: IGameActions
    userId: ID
    controller: ClientGameHandController
  }
> = ({ measurements, controller, actions, userId, ...props }) => {
  const [hasModalOpen, setHasModalOpen] = useState(false)
  const userData = controller.getUserData(userId)
  return (
    <React.Fragment>
      <Modal
        label={`Game Hand Details for ${userData.email}`}
        isOpen={hasModalOpen}
        onClose={() => setHasModalOpen(false)}
        header={
          <Txt sx={{ textAlign: 'center' }}>
            <Box sx={{ borderRadius: 'pill', p: 4, bg: 'lightGreen', display: 'inline-block' }}>
              <UserProfilePicture width={100} user={userData} />
            </Box>
            <Heading as="h3" mt={2}>
              <Ellipsis>
                <FormattedUserName user={userData} />
              </Ellipsis>
            </Heading>
          </Txt>
        }
      >
        <OtherPlayerInformationModal {...{ controller, userData, actions }} />
      </Modal>
      <Positioned
        width={measurements.offTableUserInfoWidth}
        height={measurements.offTableUserInfoHeight}
        coords={userData.calcPositionFromPixels(
          measurements.offTableUserInfoHeight / 2 + theme.space[3],
        )}
      >
        <ButtonBlock
          onClick={() => setHasModalOpen(true)}
          sx={{
            textAlign: 'center',
          }}
        >
          <BlinkingProfilePicture {...{ userData, measurements }} />
          <Ellipsis sx={{ textAlign: 'center', fontSize: [0, 0, 1] }}>{userData.email}</Ellipsis>
          <Ellipsis sx={{ textAlign: 'center', fontSize: [1, 1, 2], color: 'lightText' }}>
            <FormattedCurrency amount={userData.amountLeftToBet} />
          </Ellipsis>
        </ButtonBlock>
      </Positioned>
    </React.Fragment>
  )
}
